import moment from "moment-timezone";
import {
    ServiceDeploymentResponseResponse,
    StageDeploymentResponseLogsErrorMessages,
    StageDeploymentResponseResponse
} from "@microtica/ms-elasticsearch-sdk";
import { GetDeployedMicroservicesResponseDeployedMicroservices, MicroserviceConfigurationItem } from "@microtica/ms-kube-sdk";
import { DeploymentType } from "../types";

export const mapInfraDeployments = (deployments: StageDeploymentResponseResponse[], lastDeploymentId?: string) => {
    return deployments.map(d => {
        const prevDeployment = deployments.find(dd => dd.deploymentId === d.lastDeploymentId);
        const resources = (d.resources || []).map(resourceDetails => {
            const resourceDeployment = d.resources.find(res => res.name === resourceDetails.name)!;
            const prevResourceDeployment = (prevDeployment?.resources || []).find(res => res.name === resourceDetails.name)!;
            const { metadata } = resourceDeployment.component!;

            const changes = prevResourceDeployment ? arraysChanges(prevResourceDeployment.configurations, resourceDetails.configurations) : [];

            return {
                id: resourceDetails.name,
                name: resourceDetails.name,
                deploymentId: d.deploymentId,
                version: metadata?.commit.sha || metadata.commit.version,
                branch: metadata.commit ? metadata.commit.name : "n/a",
                commitType: metadata?.commit.type,
                commitMessage: metadata.commit ? metadata.commit.message : "n/a",
                repositoryUrl: metadata.repository,
                pipeline: {
                    id: resourceDeployment.component.pipelineId,
                    buildId: resourceDeployment.component.version,
                    public: !!resourceDeployment.component.isPublic
                },
                createdAt: moment(resourceDeployment.component.metadata.commit.date || resourceDeployment.lastDeployed).fromNow(),
                createdBy: d.initiator.email,
                initiator: metadata.commit.user,
                avatarUrl: metadata.commit.user.avatar,
                status: {
                    name: resourceDetails.status,
                    errors: d.logs?.events?.[resourceDetails.name]?.errorMessages
                },
                configurations: resourceDetails.configurations,
                timestamp: "n/a",
                prev: {
                    changes,
                    equal: !prevResourceDeployment ? false :
                        (
                            prevResourceDeployment.component.metadata.commit.sha ?
                                prevResourceDeployment.component.metadata.commit.sha === metadata.commit.sha :
                                prevResourceDeployment.component.metadata.commit.version === metadata.commit.version
                        ) &&
                        changes.length === 0,
                    version: prevResourceDeployment?.component.metadata.commit.sha || prevResourceDeployment?.component.metadata.commit.version,
                    branch: prevResourceDeployment?.component.metadata.commit.name || "n/a",
                    commitType: prevResourceDeployment?.component.metadata.commit.type,
                    repositoryUrl: prevResourceDeployment?.component.metadata.repository
                }
            }
        });

        const resourceErrors = (d.resources || []).reduce((errors, resource) => {
            const resourceErrors = d.logs?.events?.[resource.name]?.errorMessages || [];
            errors.push(...resourceErrors);
            return errors;
        }, [] as StageDeploymentResponseLogsErrorMessages[]);

        return {
            id: d.deploymentId,
            name: d.name,
            type: "infrastructure" as DeploymentType,
            active: lastDeploymentId === d.deploymentId,
            createdAt: moment(d.timestamp).fromNow(),
            createdBy: d.initiator.email,
            error: d.error,
            status: {
                name: d.status,
                errors: resourceErrors
            },
            timestamp: d.timestamp,
            resources
        }
    });
}

export const mapAppDeployments = (deployments: ServiceDeploymentResponseResponse[], apps: GetDeployedMicroservicesResponseDeployedMicroservices[]) => {
    return deployments.map(d => {
        const prevDeployment = deployments.find(dd => dd.deploymentId === d.lastDeploymentId);

        const changes = prevDeployment ? arraysChanges(
            prevDeployment.configurations.filter(c => !c.key.startsWith("MIC_")),
            d.configurations.filter(c => !c.key.startsWith("MIC_"))
        ) : [];

        return {
            id: d.deploymentId,
            name: d.name,
            type: "app" as DeploymentType,
            active: apps.find(a => a.name === d.name && a.kubernetesId === d.kubernetesId)?.deploymentId === d.deploymentId,
            createdAt: moment(d.timestamp).fromNow(),
            createdBy: d.initiator.email,
            error: (d as any).error,
            status: {
                name: d.status,
                errors: (d as any).error && [(d as any).error] as any
            },
            timestamp: d.timestamp,
            resources: [{
                id: d.id,
                name: d.name,
                deploymentId: d.id,
                kubernetesId: d.kubernetesId,
                kubernetesName: d.kubernetesName,
                kubernetesNamespace: (d as any).namespace,
                version: d.commit?.sha || d.commit?.version,
                branch: d.commit?.name || "n/a",
                commitType: d.commit?.type,
                commitMessage: d.commit?.message || "n/a",
                repositoryUrl: d.commit?.repository || d.imageRepository,
                pipeline: {
                    id: (d.commit as any)?.pipelineId,
                    buildId: d.commit?.version,
                    public: false
                },
                createdAt: moment(d.commit?.date || d.timestamp).fromNow(),
                createdBy: d.initiator.email,
                avatarUrl: d.commit?.user.avatar,
                status: {
                    name: d.status
                },
                configurations: d.configurations,
                timestamp: d.commit?.date || d.timestamp,
                prev: {
                    changes,
                    equal: !prevDeployment ? false :
                        (
                            d.commit?.sha === prevDeployment.commit?.sha ||
                            d.commit?.version === prevDeployment.commit?.version
                        ) &&
                        changes.length === 0,
                    version: prevDeployment?.commit?.sha || prevDeployment?.commit?.version!,
                    branch: prevDeployment?.commit?.name || "n/a",
                    commitType: prevDeployment?.commit?.type!,
                    repositoryUrl: prevDeployment?.commit?.repository
                }
            }]
        }
    });
}

const objectsEqual = (o1: any, o2: any) => {
    if (!o1 && !o2) {
        return true;
    } else if (!o1 || !o2) {
        return false;
    }

    return Object.keys(o1).length === Object.keys(o2).length
        && Object.keys(o1).every(p => o1[p] === o2[p]);
}

const arraysChanges = (a1: MicroserviceConfigurationItem[], a2: MicroserviceConfigurationItem[]) => {
    const changes: MicroserviceConfigurationItem[][] = [];

    let conf1: MicroserviceConfigurationItem[] = [];
    let conf2: MicroserviceConfigurationItem[] = [];
    let reverseChange = false;

    if (a1.length >= a2.length) {
        conf1 = a1;
        conf2 = a2;
        reverseChange = true;
    } else if (a1.length < a2.length) {
        conf1 = a2;
        conf2 = a1;
    }

    conf1.forEach((o: MicroserviceConfigurationItem) => {
        const a = conf2.find(a => a.key === o.key);

        if (!a) {
            return changes.push(
                reverseChange ?
                    [{ key: o.key, value: "no value" }, o].reverse() :
                    [{ key: o.key, value: "no value" }, o]
            );
        }

        const equal = objectsEqual(o, a);
        if (!equal) {
            changes.push(reverseChange ? [a, o].reverse() : [a, o]);
        }
    });

    return changes;
}